import React, { useMemo } from "react";

interface PaginationProps {
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

/**
 * Simple page navigation with first/last and a windowed range of pages.
 * Uses inline styles so it can be dropped into pages that rely on global CSS.
 */
const Pagination: React.FC<PaginationProps> = ({ page, totalPages, onPageChange }) => {
    const visiblePages = useMemo(() => {
        const range: (number | string)[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) range.push(i);
        } else {
            range.push(1);
            if (page > 3) range.push("...");
            for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
                range.push(i);
            }
            if (page < totalPages - 2) range.push("...");
            range.push(totalPages);
        }
        return range;
    }, [page, totalPages]);

    if (totalPages <= 1) return null;

    const buttonStyle: React.CSSProperties = {
        minWidth: "34px",
        height: "34px",
        padding: "0 10px",
        border: "1px solid #d0d0d0",
        borderRadius: "6px",
        background: "#fff",
        color: "#333",
        fontSize: "14px",
        lineHeight: "1",
        cursor: "pointer",
    };
    const activeStyle: React.CSSProperties = {
        ...buttonStyle,
        background: "var(--green-600)",
        border: "1px solid var(--green-600)",
        color: "#fff",
    };

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                margin: "24px 0",
                flexWrap: "wrap",
            }}
        >
            <button
                type="button"
                style={{ ...buttonStyle, opacity: page <= 1 ? 0.5 : 1 }}
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
            >
                {"<"}
            </button>
            {visiblePages.map((p, idx) =>
                typeof p === "string" ? (
                    <span key={`ellipsis-${idx}`} style={{ padding: "0 4px", color: "#333" }}>
                        {p}
                    </span>
                ) : (
                    <button
                        key={p}
                        type="button"
                        style={p === page ? activeStyle : buttonStyle}
                        onClick={() => onPageChange(p)}
                    >
                        {p}
                    </button>
                ),
            )}
            <button
                type="button"
                style={{ ...buttonStyle, opacity: page >= totalPages ? 0.5 : 1 }}
                disabled={page >= totalPages}
                onClick={() => onPageChange(page + 1)}
            >
                {">"}
            </button>
        </div>
    );
};

export default Pagination;
